import React, { useState, useEffect, useRef } from 'react';
import { firestore } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import '../../pages/Dashboard.css';

// Função Helper para normalizar texto (apenas remove espaços extras e caixa alta)
const normalizarTexto = (texto) => {
    if (!texto) return '';
    return texto.trim().toUpperCase();
};

// Constantes para Opções de Filtro
const OPCOES_STATUS = [
    { label: 'Trabalhado', value: 'INSPECIONADO' },
    { label: 'Recuperado', value: 'RECUPERADO' },
    { label: 'Recusa', value: 'RECUSA' },
    { label: 'Fechado', value: 'FECHADO' },
    { label: 'Aguardando Análise', value: 'AGUARDANDO ANÁLISE' },
    { label: 'Análise Parcial', value: 'ANÁLISE PARCIAL' },
    { label: 'Análise Concluída', value: 'ANÁLISE CONCLUÍDA' }
];

const OPCOES_PE_FILTER = [
    { label: 'Incluir Pontos Estratégicos', value: 'com_pe' },
    { label: 'Sem Pontos Estratégicos', value: 'sem_pe' },
    { label: 'Apenas Pontos Estratégicos', value: 'so_pe' }
];

// Componente MultiSelectFilter (mesmo do ResumoGeral)
const MultiSelectFilter = ({ label, options, selectedValues, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleToggle = (value) => {
        onChange(
            selectedValues.includes(value)
                ? selectedValues.filter(v => v !== value)
                : [...selectedValues, value]
        );
    };

    const handleSelectAll = (isSelectingAll) => {
        onChange(isSelectingAll ? options.map(o => o.value || o) : []);
    };

    const allSelected = selectedValues.length === options.length && options.length > 0;
    const displayLabel = selectedValues.length === 0
        ? `Todos os ${label}`
        : `${selectedValues.length} Selecionado(s)`;

    return (
        <div className="input-group multi-select-filter" ref={dropdownRef}>
            <label>{label}</label>
            <div className="dropdown">
                <button
                    className="dropdown-toggle"
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {displayLabel}
                </button>
                {isOpen && (
                    <div className="dropdown-menu">
                        <label>
                            <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={() => handleSelectAll(!allSelected)}
                            />
                            **Selecionar Todos**
                        </label>
                        <hr />
                        {options.map(option => (
                            <label key={option.value || option}>
                                <input
                                    type="checkbox"
                                    value={option.value || option}
                                    checked={selectedValues.includes(option.value || option)}
                                    onChange={() => handleToggle(option.value || option)}
                                />
                                {option.label || option}
                            </label>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const gerarAnaliseDetalhada = (visitas) => {
    const analise = {
        exemplares: {
            aegypti: { larvas: 0, pupas: 0, exuvia: 0, adultos: 0, total: 0 },
            albopictus: { larvas: 0, pupas: 0, exuvia: 0, adultos: 0, total: 0 },
            outros: { larvas: 0, pupas: 0, exuvia: 0, adultos: 0, total: 0 },
        },
        depositosPositivos: {
            A1: { aegypti: 0, albopictus: 0, outros: 0, total: 0 }, A2: { aegypti: 0, albopictus: 0, outros: 0, total: 0 },
            B: { aegypti: 0, albopictus: 0, outros: 0, total: 0 }, C: { aegypti: 0, albopictus: 0, outros: 0, total: 0 },
            D1: { aegypti: 0, albopictus: 0, outros: 0, total: 0 }, D2: { aegypti: 0, albopictus: 0, outros: 0, total: 0 },
            E: { aegypti: 0, albopictus: 0, outros: 0, total: 0 }, Total: 0
        },
        imoveisPositivos: {
            R: { aegypti: 0, albopictus: 0, outros: 0, total: 0 }, C: { aegypti: 0, albopictus: 0, outros: 0, total: 0 },
            TB: { aegypti: 0, albopictus: 0, outros: 0, total: 0 }, PE: { aegypti: 0, albopictus: 0, outros: 0, total: 0 },
            O: { aegypti: 0, albopictus: 0, outros: 0, total: 0 }, Total: 0
        },
        localidades: {},
    };

    const imoveisJaContados = new Set();

    for (const v of visitas) {
        if (!v.amostras || v.amostras.length === 0) continue;

        for (const amostra of v.amostras) {
            // Verifica se o status é concluído (considerando variações de caixa alta/baixa e acentuação)
            const statusAnalise = amostra.analise?.status ? String(amostra.analise.status).toUpperCase() : '';
            if (!amostra.analise || (statusAnalise !== 'CONCLUIDO' && statusAnalise !== 'CONCLUÍDO')) continue;

            const { aegypti, albopictus, outros } = amostra.analise;
            const especies = { aegypti, albopictus, outros };

            for (const [nomeEspecie, exemplares] of Object.entries(especies)) {
                if (!exemplares) continue;
                const totalExemplaresNaAmostra = (exemplares.larvas || 0) + (exemplares.pupas || 0) + (exemplares.exuvia || 0) + (exemplares.adultos || 0);

                if (totalExemplaresNaAmostra > 0) {
                    // Contagem por Estágio
                    analise.exemplares[nomeEspecie].larvas += exemplares.larvas || 0;
                    analise.exemplares[nomeEspecie].pupas += exemplares.pupas || 0;
                    analise.exemplares[nomeEspecie].exuvia += exemplares.exuvia || 0;
                    analise.exemplares[nomeEspecie].adultos += exemplares.adultos || 0;

                    // Contagem detalhada por Depósito
                    const dep = amostra.depositoColeta;
                    if (dep && analise.depositosPositivos[dep]) {
                        analise.depositosPositivos[dep][nomeEspecie]++;
                    }

                    // Contagem detalhada por Imóvel
                    const imovelId = v.id;
                    const chaveContagemImovel = `${imovelId}-${nomeEspecie}`;
                    if (!imoveisJaContados.has(chaveContagemImovel)) {
                        const imovel = v.tipoImovel;
                        if (imovel && analise.imoveisPositivos[imovel]) {
                            analise.imoveisPositivos[imovel][nomeEspecie]++;
                            imoveisJaContados.add(chaveContagemImovel);
                        }
                    }

                    // Contagem por Localidade/Quarteirão
                    const local = v.localidade;
                    const quart = v.quarteirao;
                    if (!analise.localidades[local]) analise.localidades[local] = {};
                    if (!analise.localidades[local][quart]) analise.localidades[local][quart] = { aegypti: 0, albopictus: 0, outros: 0 };
                    analise.localidades[local][quart][nomeEspecie]++;
                }
            }
        }
    }

    // Calcula todos os totais no final
    for (const especie of Object.keys(analise.exemplares)) {
        analise.exemplares[especie].total = analise.exemplares[especie].larvas + analise.exemplares[especie].pupas + analise.exemplares[especie].exuvia + analise.exemplares[especie].adultos;
    }
    for (const tipo of Object.keys(analise.depositosPositivos)) {
        if (tipo !== 'Total') {
            const item = analise.depositosPositivos[tipo];
            item.total = item.aegypti + item.albopictus + item.outros;
            analise.depositosPositivos.Total += item.total;
        }
    }
    for (const tipo of Object.keys(analise.imoveisPositivos)) {
        if (tipo !== 'Total') {
            const item = analise.imoveisPositivos[tipo];
            item.total = item.aegypti + item.albopictus + item.outros;
            analise.imoveisPositivos.Total += item.total;
        }
    }

    return analise;
};

function ResumoGeralSupervisor({ userLocalidades }) {
    const [agentsList, setAgentsList] = useState([]);
    const [selectedAgents, setSelectedAgents] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [selectedLocalidade, setSelectedLocalidade] = useState('');
    const [selectedPEFilter, setSelectedPEFilter] = useState('com_pe');

    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    // Função para calcular a data limite (máximo 7 dias de intervalo)
    const getMaxDate = (start) => {
        if (!start) return '';
        const d = new Date(start);
        d.setDate(d.getDate() + 6); // 7 dias de intervalo (inclusive)
        return d.toISOString().split('T')[0];
    };

    // Handler para mudança na Data Inicial
    const handleStartDateChange = (e) => {
        const newStart = e.target.value;
        setStartDate(newStart);

        // Se a data final atual for menor que a nova data inicial ou exceder o limite de 7 dias, ajusta automaticamente
        if (newStart && endDate) {
            const maxDate = getMaxDate(newStart);
            if (endDate < newStart) {
                setEndDate(newStart);
            } else if (endDate > maxDate) {
                setEndDate(maxDate);
            }
        }
    };

    const [cachedVisitas, setCachedVisitas] = useState([]);
    const [dadosResumo, setDadosResumo] = useState(null);
    const [analiseDetalhada, setAnaliseDetalhada] = useState(null);
    const [loading, setLoading] = useState(false);
    const [lastFetchParams, setLastFetchParams] = useState(null);

    // Buscar agentes
    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const q = query(collection(firestore, "usuarios"), where("perfil", "==", "agente"));
                const querySnapshot = await getDocs(q);
                const agents = querySnapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() }));
                setAgentsList(agents);
                setSelectedAgents(agents.map(a => a.uid));
            } catch (error) {
                console.error("Erro ao buscar agentes: ", error);
            }
        };
        fetchAgents();
    }, []);

    // Inicializar status e localidades selecionados
    useEffect(() => {
        setSelectedStatus(OPCOES_STATUS.map(o => o.value));
        if (userLocalidades && userLocalidades.length > 0) {
            setSelectedLocalidade(userLocalidades[0]); // Seleciona a primeira localidade por padrão
        }
    }, [userLocalidades]);

    // Função de cálculo de resumos
    const calcularResumos = (visitas) => {
        const resumo = {
            imoveisTrabalhados: { R: 0, C: 0, TB: 0, PE: 0, O: 0, Total: 0 },
            statusImoveis: { INSPECIONADO: 0, RECUPERADO: 0, RECUSA: 0, FECHADO: 0 },
            depositosInspecionados: { A1: 0, A2: 0, B: 0, C: 0, D1: 0, D2: 0, E: 0, Total: 0 },
            depositosEliminados: 0,
            tubitosColetados: 0,
            quarteiroesTrabalhados: new Set(),
            tratamentoFocal: { imoveis: 0, gramas: 0, depositos: 0 },
            tratamentoPerifocal: { imoveis: 0, gramas: 0 },
            resumoAnimais: {
                total: 0, caes: 0, gatos: 0, machos: 0, femeas: 0, emResidencias: 0, emComercios: 0,
            }
        };

        for (const v of visitas) {
            if (v.tipoImovel && resumo.imoveisTrabalhados.hasOwnProperty(v.tipoImovel)) {
                resumo.imoveisTrabalhados[v.tipoImovel]++;
                resumo.imoveisTrabalhados.Total++;
            }
            if (v.visitaStatus) {
                const status = v.visitaStatus;
                if (resumo.statusImoveis.hasOwnProperty(status)) {
                    resumo.statusImoveis[status]++;
                }
            }
            if (v.depositos) {
                for (const tipo of ['A1', 'A2', 'B', 'C', 'D1', 'D2', 'E']) {
                    const count = Number(v.depositos[tipo]) || 0;
                    if (count > 0) {
                        resumo.depositosInspecionados[tipo] += count;
                        resumo.depositosInspecionados.Total += count;
                    }
                }
            }
            resumo.depositosEliminados += Number(v.depositoEliminado) || 0;
            resumo.tubitosColetados += Number(v.qtdeTubitos) || (v.amostras ? v.amostras.length : 0);
            if (v.quarteirao) {
                resumo.quarteiroesTrabalhados.add(v.quarteirao);
            }

            // --- CÁLCULO DE TRATAMENTO FOCAL ---
            if (v.tratamentosFocais && Array.isArray(v.tratamentosFocais) && v.tratamentosFocais.length > 0) {
                resumo.tratamentoFocal.imoveis++;
                resumo.tratamentoFocal.depositos += v.tratamentosFocais.length;
                v.tratamentosFocais.forEach(t => {
                    resumo.tratamentoFocal.gramas += Number(t.qtdeGramas) || 0;
                });
            } else if (v.tratFocalImovelTratado) {
                resumo.tratamentoFocal.imoveis++;
            }

            // --- CÁLCULO DE TRATAMENTO PERIFOCAL ---
            if (v.tratamentosPerifocais && Array.isArray(v.tratamentosPerifocais) && v.tratamentosPerifocais.length > 0) {
                resumo.tratamentoPerifocal.imoveis++;
                v.tratamentosPerifocais.forEach(t => {
                    resumo.tratamentoPerifocal.gramas += Number(t.qtdeGramas) || 0;
                });
            } else if (v.tratPerifocalImovelTratado) {
                resumo.tratamentoPerifocal.imoveis++;
            }

            // --- CÁLCULO DE ANIMAIS ---
            if (v.temAnimais === 'SIM' && v.animais && Array.isArray(v.animais)) {
                for (const animal of v.animais) {
                    const quantidade = Number(animal.quantidade) || 0;
                    resumo.resumoAnimais.total += quantidade;
                    if (animal.tipo === 'CAO') resumo.resumoAnimais.caes += quantidade;
                    if (animal.tipo === 'GATO') resumo.resumoAnimais.gatos += quantidade;
                    if (animal.sexo === 'MACHO') resumo.resumoAnimais.machos += quantidade;
                    if (animal.sexo === 'FEMEA') resumo.resumoAnimais.femeas += quantidade;
                    if (v.tipoImovel === 'R') resumo.resumoAnimais.emResidencias += quantidade;
                    if (v.tipoImovel === 'C') resumo.resumoAnimais.emComercios += quantidade;
                }
            }
        }
        return resumo;
    };

    // --- 1. BUSCA NO BANCO (Data + Localidade) ---
    const handleFetchData = async () => {
        if (!startDate || !endDate) {
            alert("Por favor, selecione a data de início e de fim.");
            return;
        }

        if (!selectedLocalidade) {
            alert("Por favor, selecione a Localidade para buscar.");
            return;
        }

        // Math.ceil com end=23:59:59 e start=00:00:00 de uma semana inteira (dom-sáb) = 7. Limite correto é > 7.
        const diffTime = Math.abs((new Date(endDate)) - (new Date(startDate)));
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 7) {
            alert("O período máximo de busca é de 7 dias.");
            return;
        }

        setLoading(true);
        setCachedVisitas([]);
        setDadosResumo(null);
        setAnaliseDetalhada(null);

        const [sYear, sMonth, sDay] = startDate.split('-');
        const start = new Date(sYear, sMonth - 1, sDay);
        start.setHours(0, 0, 0, 0);

        const [eYear, eMonth, eDay] = endDate.split('-');
        const end = new Date(eYear, eMonth - 1, eDay);
        end.setHours(23, 59, 59, 999);

        // Para Supervisores, filtramos por Data E Localidade no Banco (Server-side)
        // Isso é eficiente pois Supervisor olha uma localidade por vez.
        const localidadeNormalized = normalizarTexto(selectedLocalidade);

        const q = query(
            collection(firestore, 'relatoriosDiarios_FAD1'),
            where('dataAtividade', '>=', start),
            where('dataAtividade', '<=', end),
            where('localidade', '==', localidadeNormalized)
        );

        try {
            const querySnapshot = await getDocs(q);
            let visitas = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Filtrar visitas canceladas (ROBUSTO)
            visitas = visitas.filter(v => !v.statusRegistro || v.statusRegistro.trim().toLowerCase() === 'ativo');

            console.log(`[Supervisor] Baixados ${visitas.length} registros para ${selectedLocalidade} (excluindo canceladas).`);
            setCachedVisitas(visitas);
            setLastFetchParams({ start: startDate, end: endDate, loc: selectedLocalidade, count: visitas.length });

        } catch (error) {
            console.error("Erro ao buscar dados:", error);
            alert("Erro ao buscar dados.");
        }
        setLoading(false);
    };

    // --- 2. FILTRAGEM LOCAL ---
    useEffect(() => {
        if (cachedVisitas.length === 0) return;

        const filtrarEProcessar = () => {
            const visitas = cachedVisitas.filter(v => {
                if (selectedAgents.length > 0 && !selectedAgents.includes(v.idAgente)) return false;
                
                if (selectedStatus.length > 0) {
                    const statusVisita = v.visitaStatus ? String(v.visitaStatus).trim().toUpperCase() : '';
                    const statusLab = v.status ? String(v.status).trim().toUpperCase() : '';
                    if (!selectedStatus.includes(statusVisita) && !selectedStatus.includes(statusLab)) {
                        return false;
                    }
                }

                if (selectedPEFilter === 'sem_pe' && v.tipoImovel === 'PE') return false;
                if (selectedPEFilter === 'so_pe' && v.tipoImovel !== 'PE') return false;
                return true;
            });

            console.log(`[Supervisor] Filtrado localmente: ${visitas.length} registros.`);
            setDadosResumo(calcularResumos(visitas));
            setAnaliseDetalhada(gerarAnaliseDetalhada(visitas));
        };

        filtrarEProcessar();
    }, [cachedVisitas, selectedAgents, selectedStatus, selectedPEFilter]);

    if (!userLocalidades || userLocalidades.length === 0) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <h3>⚠️ Nenhuma localidade atribuída</h3>
                <p style={{ color: '#666' }}>
                    Entre em contato com o gestor para atribuir localidades ao seu perfil.
                </p>
            </div>
        );
    }

    return (
        <div className="resumo-geral">
            <h2>Resumo Geral - Suas Localidades</h2>
            <p style={{ color: '#666', marginBottom: '20px' }}>
                Localidades: <strong>{userLocalidades.join(', ')}</strong>
            </p>

            <div className="filters-container" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>

                {/* --- SEÇÃO 1: FETCH (Consome Leituras) --- */}
                <div style={{ padding: '15px', backgroundColor: '#eef2f7', borderRadius: '8px', border: '1px solid #cce' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#335' }}>1. Seleção de Dados</h4>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div className="input-group" style={{ flex: 1, minWidth: '200px' }}>
                            <label>Localidade</label>
                            <select
                                value={selectedLocalidade}
                                onChange={(e) => setSelectedLocalidade(e.target.value)}
                                style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ddd', width: '100%' }}
                            >
                                <option value="">Selecione...</option>
                                {userLocalidades && userLocalidades.map(loc => (
                                    <option key={loc} value={loc}>{loc}</option>
                                ))}
                            </select>
                        </div>
                        <div className="input-group">
                            <label>Início</label>
                            <input type="date" value={startDate} onChange={handleStartDateChange} />
                        </div>
                        <div className="input-group">
                            <label>Fim</label>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate} max={getMaxDate(startDate)} />
                        </div>
                        <button
                            onClick={handleFetchData}
                            disabled={loading || !selectedLocalidade}
                            style={{ backgroundColor: '#2c5282', color: 'white', height: '42px', marginBottom: '2px' }}
                        >
                            {loading ? '...' : '📥 Baixar'}
                        </button>
                    </div>
                    {lastFetchParams && (
                        <p style={{ fontSize: '0.85em', color: '#666', marginTop: '5px', marginBottom: 0 }}>
                            ✅ Baixado: {lastFetchParams.count} registros de {lastFetchParams.loc}.
                        </p>
                    )}
                </div>

                {/* --- SEÇÃO 2: FILTROS LOCAIS --- */}
                <div style={{ padding: '15px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #ddd' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#555' }}>2. Filtros de Visualização</h4>
                    <div className="filter-row" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1 }}>
                            <MultiSelectFilter
                                label="Agentes"
                                options={agentsList.map(a => ({ label: a.nome || a.email, value: a.uid }))}
                                selectedValues={selectedAgents}
                                onChange={setSelectedAgents}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <MultiSelectFilter
                                label="Status"
                                options={OPCOES_STATUS}
                                selectedValues={selectedStatus}
                                onChange={setSelectedStatus}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div className="input-group">
                                <label>Pontos Estratégicos</label>
                                <select
                                    value={selectedPEFilter}
                                    onChange={(e) => setSelectedPEFilter(e.target.value)}
                                    style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ddd', width: '100%' }}
                                >
                                    {OPCOES_PE_FILTER.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {loading && <p>Calculando resumos...</p>}

            {dadosResumo && (
                <div className="summary-grid">
                    <div className="summary-item">
                        <h4>Nº Imóveis trabalhados por tipo</h4>
                        <div className="resumo-details">
                            <p>Residências (R): <strong>{dadosResumo?.imoveisTrabalhados.R || 0}</strong></p>
                            <p>Comércios (C): <strong>{dadosResumo?.imoveisTrabalhados.C || 0}</strong></p>
                            <p>Terrenos Baldios (TB): <strong>{dadosResumo?.imoveisTrabalhados.TB || 0}</strong></p>
                            <p>Pontos Estratégicos (PE): <strong>{dadosResumo?.imoveisTrabalhados.PE || 0}</strong></p>
                            <p>Outros (O): <strong>{dadosResumo?.imoveisTrabalhados.O || 0}</strong></p>
                            <p className="total">Total: <strong>{dadosResumo?.imoveisTrabalhados.Total || 0}</strong></p>
                        </div>
                    </div>

                    <div className="summary-item">
                        <h4>Status dos Imóveis</h4>
                        <div className="resumo-details">
                            <p>Trabalhados: <strong>{dadosResumo?.statusImoveis.INSPECIONADO || 0}</strong></p>
                            <p>Recuperados: <strong>{dadosResumo?.statusImoveis.RECUPERADO || 0}</strong></p>
                            <p>Recusas: <strong>{dadosResumo?.statusImoveis.RECUSA || 0}</strong></p>
                            <p>Fechados: <strong>{dadosResumo?.statusImoveis.FECHADO || 0}</strong></p>
                        </div>
                    </div>

                    <div className="summary-item">
                        <h4>Nº depósitos inspecionados</h4>
                        <div className="resumo-details">
                            <p>A1: <strong>{dadosResumo?.depositosInspecionados.A1 || 0}</strong></p>
                            <p>A2: <strong>{dadosResumo?.depositosInspecionados.A2 || 0}</strong></p>
                            <p>B: <strong>{dadosResumo?.depositosInspecionados.B || 0}</strong></p>
                            <p>C: <strong>{dadosResumo?.depositosInspecionados.C || 0}</strong></p>
                            <p>D1: <strong>{dadosResumo?.depositosInspecionados.D1 || 0}</strong></p>
                            <p>D2: <strong>{dadosResumo?.depositosInspecionados.D2 || 0}</strong></p>
                            <p>E: <strong>{dadosResumo?.depositosInspecionados.E || 0}</strong></p>
                            <p className="total">Total: <strong>{dadosResumo?.depositosInspecionados.Total || 0}</strong></p>
                        </div>
                    </div>

                    <div className="summary-item">
                        <h4>Tratamento Focal Larvicida</h4>
                        <div className="resumo-details">
                            <p>Imóveis Tratados: <strong>{dadosResumo?.tratamentoFocal.imoveis || 0}</strong></p>
                            <p>Depósitos Tratados: <strong>{dadosResumo?.tratamentoFocal.depositos || 0}</strong></p>
                            <p>Gramas de Larvicida: <strong>{dadosResumo?.tratamentoFocal.gramas.toFixed(1) || 0}g</strong></p>
                        </div>
                    </div>

                    <div className="summary-item">
                        <h4>Tratamento Perifocal Adulticida</h4>
                        <div className="resumo-details">
                            <p>Imóveis Tratados: <strong>{dadosResumo?.tratamentoPerifocal.imoveis || 0}</strong></p>
                            <p>Gramas de Adulticida: <strong>{dadosResumo?.tratamentoPerifocal.gramas.toFixed(1) || 0}g</strong></p>
                        </div>
                    </div>

                    <div className="summary-item">
                        <h4>Censo Animal</h4>
                        <div className="resumo-details">
                            <p>Cães: {dadosResumo?.resumoAnimais.caes || 0} | Gatos: {dadosResumo?.resumoAnimais.gatos || 0}</p>
                            <p>Machos: {dadosResumo?.resumoAnimais.machos || 0} | Fêmeas: {dadosResumo?.resumoAnimais.femeas || 0}</p>
                            <hr style={{ margin: '5px 0', borderColor: '#eee' }} />
                            <p><strong>Total de Animais:</strong> {dadosResumo?.resumoAnimais.total || 0}</p>
                        </div>
                    </div>

                    <div className="summary-item">
                        <h4>Atividades Gerais</h4>
                        <div className="resumo-details">
                            <p>Depósitos Eliminados: <strong>{dadosResumo?.depositosEliminados || 0}</strong></p>
                            <p>Tubitos Coletados: <strong>{dadosResumo?.tubitosColetados || 0}</strong></p>
                            <p>Quarteirões Trabalhados: <strong>{dadosResumo?.quarteiroesTrabalhados.size || 0}</strong></p>
                        </div>
                    </div>
                </div>
            )}

            {analiseDetalhada && (
                <>
                    <h3 style={{ marginTop: '30px', borderTop: '1px solid #eee', paddingTop: '20px' }}>Análise Laboratorial Detalhada</h3>
                    <div className="summary-gridLab" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginTop: '15px' }}>
                        {/* Card 1: Exemplares por Espécie */}
                        <div className="summary-itemLab" style={{ background: 'white', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <h4>Exemplares por Espécie</h4>
                            <div className="resumo-details">
                                <p><strong>A. aegypti:</strong> {analiseDetalhada.exemplares.aegypti.total}
                                    <span style={{ fontSize: '0.85em', color: '#666' }}> (L:{analiseDetalhada.exemplares.aegypti.larvas}, P:{analiseDetalhada.exemplares.aegypti.pupas}, E:{analiseDetalhada.exemplares.aegypti.exuvia}, A:{analiseDetalhada.exemplares.aegypti.adultos})</span>
                                </p>
                                <p><strong>A. albopictus:</strong> {analiseDetalhada.exemplares.albopictus.total}
                                    <span style={{ fontSize: '0.85em', color: '#666' }}> (L:{analiseDetalhada.exemplares.albopictus.larvas}, P:{analiseDetalhada.exemplares.albopictus.pupas}, E:{analiseDetalhada.exemplares.albopictus.exuvia}, A:{analiseDetalhada.exemplares.albopictus.adultos})</span>
                                </p>
                            </div>
                        </div>

                        {/* Card 2: Depósitos com Espécimes */}
                        <div className="summary-itemLab" style={{ background: 'white', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <h4>Depósitos com Espécimes</h4>
                            <div className="resumo-details">
                                {Object.entries(analiseDetalhada.depositosPositivos).map(([tipo, especies]) => {
                                    if (tipo === 'Total') return null;
                                    const total = especies.aegypti + especies.albopictus + especies.outros;
                                    return (
                                        <p key={tipo}>
                                            <strong>{tipo}:</strong> {total}
                                            <span style={{ fontSize: '0.85em', color: '#666' }}> (Aeg: {especies.aegypti}, Alb: {especies.albopictus})</span>
                                        </p>
                                    );
                                })}
                                <p className="total" style={{ marginTop: '10px', fontWeight: 'bold' }}>Total de Depósitos Positivos: <strong>{analiseDetalhada.depositosPositivos.Total || 0}</strong></p>
                            </div>
                        </div>

                        {/* Card 3: Imóveis com Espécimes */}
                        <div className="summary-itemLab" style={{ background: 'white', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                            <h4>Imóveis com Espécimes</h4>
                            <div className="resumo-details">
                                {Object.entries(analiseDetalhada.imoveisPositivos).map(([tipo, especies]) => {
                                    if (tipo === 'Total') return null;
                                    const total = especies.aegypti + especies.albopictus + especies.outros;
                                    return (
                                        <p key={tipo}>
                                            <strong>{tipo}:</strong> {total}
                                            <span style={{ fontSize: '0.85em', color: '#666' }}> (Aeg: {especies.aegypti}, Alb: {especies.albopictus})</span>
                                        </p>
                                    );
                                })}
                                <p className="total" style={{ marginTop: '10px', fontWeight: 'bold' }}>Total de Imóveis Positivos: <strong>{analiseDetalhada.imoveisPositivos.Total || 0}</strong></p>
                            </div>
                        </div>

                        {/* Card 4: Positividade por Localidade e Quarteirão */}
                        {Object.keys(analiseDetalhada.localidades).length > 0 && (
                            <div className="summary-itemLab" style={{ background: 'white', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                <h4>Positividade por Localidade / Quarteirão</h4>
                                <div className="resumo-details">
                                    {Object.entries(analiseDetalhada.localidades).map(([local, quarteiroes]) => (
                                        <div key={local} className="location-group" style={{ marginBottom: '10px' }}>
                                            <strong>{local}:</strong>
                                            {Object.entries(quarteiroes).map(([quart, especies]) => (
                                                <p key={quart} style={{ marginLeft: '1rem', fontSize: '0.9em' }}>
                                                    - Quarteirão {quart}:
                                                    <span style={{ fontSize: '0.85em', color: '#666' }}> (Aegypti: {especies.aegypti}, Albopictus: {especies.albopictus})</span>
                                                </p>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default ResumoGeralSupervisor;
